import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PAPEIS_USUARIA, type PapelUsuaria } from '@reducee/contracts';
import { useState, type FormEvent } from 'react';

import { Botao, CabecalhoDeTabela, CLASSE_TABELA } from './Superficie.tsx';
import { chamarApi, ErroDaApi } from '../api/cliente.ts';
import { ROTULO_PAPEL, useSessao } from '../autenticacao/sessao.tsx';
import { CampoSelecao, CampoSenha, CampoTexto } from './Campo.tsx';
import { Dialogo } from './Dialogo.tsx';
import { RecusaDaApi } from './RecusaDaApi.tsx';

type Usuaria = {
  id: string;
  nome: string;
  email: string;
  papel: PapelUsuaria;
  ativa: boolean;
  recebeNovasEntradas: boolean;
  criadaEm: string;
};

/**
 * Administração de acessos. A API recusa este conteúdo para quem não é
 * Administradora.
 *
 * Mora em componente porque aparece em dois lugares: embutido no acordeão de
 * Configurações, que é por onde a operação chega, e na rota `/usuarias`, que
 * continua valendo para quem tem o endereço.
 */
export function UsuariasEPermissoes(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [formularioAberto, setFormularioAberto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['usuarias'],
    queryFn: () => chamarApi<Usuaria[]>('/usuarias'),
  });

  const criar = useMutation({
    mutationFn: (dados: { nome: string; email: string; senha: string; papel: PapelUsuaria }) =>
      chamarApi<Usuaria>('/usuarias', { metodo: 'POST', corpo: dados }),
    onSuccess: async () => {
      setFormularioAberto(false);
      setErro(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['usuarias'] });
    },
    onError: (causa: unknown) => {
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível salvar.');
    },
  });

  const definirDestinataria = useMutation({
    mutationFn: (id: string) =>
      chamarApi<Usuaria>(`/usuarias/${id}`, {
        metodo: 'PATCH',
        corpo: { recebeNovasEntradas: true },
      }),
    onSuccess: async () => {
      setErro(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['usuarias'] });
    },
    onError: (causa: unknown) => {
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível atualizar.');
    },
  });

  const [editando, definirEditando] = useState<Usuaria | null>(null);

  const alternarAtiva = useMutation({
    mutationFn: ({ id, ativa }: { id: string; ativa: boolean }) =>
      chamarApi<Usuaria>(`/usuarias/${id}`, { metodo: 'PATCH', corpo: { ativa } }),
    onSuccess: async () => {
      setErro(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['usuarias'] });
    },
    onError: (causa: unknown) => {
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível atualizar.');
    },
  });

  function aoEnviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const campos = new FormData(evento.currentTarget);
    criar.mutate({
      nome: String(campos.get('nome') ?? ''),
      email: String(campos.get('email') ?? ''),
      senha: String(campos.get('senha') ?? ''),
      papel: String(campos.get('papel') ?? 'COMERCIAL') as PapelUsuaria,
    });
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-texto-suave">
          Papéis organizacionais: a permissão acompanha o papel, não a pessoa.
        </p>
        <button
          type="button"
          onClick={() => setFormularioAberto((aberto) => !aberto)}
          className="rounded-lg bg-marinho px-4 py-2 text-sm font-medium text-white transition hover:bg-marinho-claro"
        >
          Nova usuária
        </button>
      </div>

      {erro ? (
        <p role="alert" className="mt-4 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          {erro}
        </p>
      ) : null}

      {formularioAberto ? (
        <form
          onSubmit={aoEnviar}
          className="mt-4 grid gap-4 rounded-xl bg-superficie p-5 ring-1 ring-linha md:grid-cols-4"
          // Embutido em Configurações, este "Nome" divide a tela com o de
          // Parceiros comerciais. Sem um alvo próprio, procurar o campo pelo
          // rótulo acha os dois.
          data-teste="formulario-de-usuaria"
        >
          <CampoTexto rotulo="Nome" name="nome" required />
          <CampoTexto rotulo="E-mail" name="email" type="email" required />
          <CampoSenha rotulo="Senha" name="senha" required minLength={10} autoComplete="new-password" />
          <CampoSelecao rotulo="Papel" name="papel" defaultValue="COMERCIAL">
            {PAPEIS_USUARIA.map((papel) => (
              <option key={papel} value={papel}>
                {ROTULO_PAPEL[papel]}
              </option>
            ))}
          </CampoSelecao>
          <div className="md:col-span-4">
            <button
              type="submit"
              disabled={criar.isPending}
              className="rounded-lg bg-marinho px-4 py-2 text-sm font-medium text-white transition hover:bg-marinho-claro disabled:opacity-60"
            >
              Salvar
            </button>
          </div>
        </form>
      ) : null}

      <div className="mt-6 overflow-x-auto rounded-xl bg-superficie ring-1 ring-linha">
        <table className={CLASSE_TABELA}>
          <CabecalhoDeTabela colunas={['Nome', 'E-mail', 'Papel', 'Situação', 'Recebe entradas', 'Ações']} />
          <tbody>
            {consulta.data?.map((usuaria) => (
              <tr key={usuaria.id} className="border-b border-linha last:border-0">
                <td className="px-3.25 py-3 font-medium">{usuaria.nome}</td>
                <td className="px-3.25 py-3">{usuaria.email}</td>
                <td className="px-3.25 py-3">{ROTULO_PAPEL[usuaria.papel]}</td>
                <td className="px-3.25 py-3">{usuaria.ativa ? 'Ativa' : 'Inativa'}</td>
                <td className="px-3.25 py-3">
                  {usuaria.recebeNovasEntradas ? (
                    <span className="rounded-full bg-marinho-suave px-2 py-0.5 text-xs font-medium text-marinho">
                      Destinatária
                    </span>
                  ) : usuaria.ativa && usuaria.papel === 'ADMINISTRADORA' ? (
                    <button
                      type="button"
                      onClick={() => definirDestinataria.mutate(usuaria.id)}
                      className="rounded-lg border border-linha px-3 py-1 transition hover:bg-marinho-suave"
                    >
                      Tornar destinatária
                    </button>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-3.25 py-3">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-teste="editar-usuaria"
                      onClick={() => definirEditando(usuaria)}
                      className="rounded-lg border border-linha px-3 py-1 transition hover:bg-marinho-suave"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        alternarAtiva.mutate({ id: usuaria.id, ativa: !usuaria.ativa })
                      }
                      className="rounded-lg border border-linha px-3 py-1 transition hover:bg-marinho-suave"
                    >
                      {usuaria.ativa ? 'Desativar' : 'Reativar'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {consulta.isLoading ? <p className="p-4 text-texto-suave">Carregando…</p> : null}
      </div>

      {editando ? (
        <EditarUsuaria usuaria={editando} aoFechar={() => definirEditando(null)} />
      ) : null}
    </div>
  );
}

/**
 * Nome, e-mail de login e senha de outra conta.
 *
 * É o único lugar onde o e-mail de login muda: Meu perfil o mostra, mas não o
 * edita. E é o caminho de volta para quem esqueceu a senha, já que o sistema
 * não manda e-mail de recuperação.
 */
function EditarUsuaria({
  usuaria,
  aoFechar,
}: {
  usuaria: Usuaria;
  aoFechar: () => void;
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const { usuaria: logada } = useSessao();
  // A própria senha passa por Meu perfil, que pede a atual. Trocá-la por aqui
  // encerraria a sessão de quem está salvando, sem aviso.
  const ehAPropria = logada?.id === usuaria.id;

  const salvar = useMutation({
    mutationFn: (dados: { nome?: string; email?: string; senha?: string }) =>
      chamarApi<Usuaria>(`/usuarias/${usuaria.id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: ['usuarias'] });
      aoFechar();
    },
  });

  function aoEnviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const campos = new FormData(evento.currentTarget);
    const nome = String(campos.get('nome') ?? '').trim();
    const email = String(campos.get('email') ?? '').trim().toLowerCase();
    const senha = String(campos.get('senha') ?? '');

    // Só vai o que mudou: a API audita cada campo recebido, e reenviar o nome
    // igual registraria uma troca que não houve.
    const dados = {
      ...(nome !== usuaria.nome ? { nome } : {}),
      ...(email !== usuaria.email ? { email } : {}),
      ...(senha ? { senha } : {}),
    };
    if (Object.keys(dados).length === 0) {
      aoFechar();
      return;
    }
    salvar.mutate(dados);
  }

  return (
    <Dialogo area="Usuárias e permissões" titulo={`Editar ${usuaria.nome}`} aoFechar={aoFechar}>
      <form
        onSubmit={aoEnviar}
        data-teste="formulario-de-edicao-de-usuaria"
        className="grid gap-3.5 md:grid-cols-2"
      >
        <CampoTexto
          rotulo="Nome"
          name="nome"
          defaultValue={usuaria.nome}
          required
          minLength={2}
          maxLength={160}
        />
        <CampoTexto
          rotulo="E-mail de login"
          name="email"
          type="email"
          defaultValue={usuaria.email}
          required
          dica="É com ele que ela entra. Confira antes de salvar."
        />
        {ehAPropria ? (
          <p className="text-xs text-texto-suave md:col-span-2">
            A sua própria senha se troca em Meu perfil, que pede a senha atual.
          </p>
        ) : (
          <CampoSenha
            rotulo="Nova senha"
            name="senha"
            minLength={10}
            maxLength={200}
            autoComplete="new-password"
            className="md:col-span-2"
            dica="Em branco, a senha continua a mesma. Trocar encerra as sessões abertas dela."
          />
        )}
        <div className="flex flex-wrap gap-2 md:col-span-2">
          <Botao tipo="submit" variante="primario" teste="salvar-usuaria" desabilitado={salvar.isPending}>
            Salvar
          </Botao>
          <Botao onClick={aoFechar}>Cancelar</Botao>
        </div>
        <div className="md:col-span-2">
          <RecusaDaApi erro={salvar.error} padrao="Não foi possível salvar." />
        </div>
      </form>
    </Dialogo>
  );
}
